<script lang="ts" setup>
import { nextTick, onUnmounted, ref, watch } from 'vue'
import { useScrollbarVisibility } from '@/composables/useScrollbarVisibility.ts'

const {
  persistent = false,
  closeOnClickOutside = false,
  onClose = undefined,
} = defineProps<{
  persistent?: boolean
  closeOnClickOutside?: boolean
  onClose?: () => void
}>()

const model = defineModel<boolean>({ default: false })

defineSlots<{
  default(_: { close: () => void }): void
}>()

const dialog = ref<HTMLDivElement | null>(null)

function close() {
  if (persistent) {
    return
  }

  if (onClose !== undefined) {
    onClose()
  } else {
    model.value = false
  }
}

const mousedown = ref(false)

function onOverlayMouseDown() {
  mousedown.value = true
}

function onOverlayMouseUp() {
  if (mousedown.value) {
    if (closeOnClickOutside) {
      close()
    }
    mousedown.value = false
  }
}

function onDialogMousedown() {
  mousedown.value = false
}

const modal = ref<HTMLElement | undefined>()

const { onDialogOpened, onDialogClosed } = useScrollbarVisibility()

watch(
  model,
  async v => {
    if (v) {
      // Add keyboard focus to enable scrolling with arrows.
      await nextTick(() => modal.value?.focus())
      onDialogOpened()
    } else {
      onDialogClosed()
    }
  },
  { immediate: true },
)

onUnmounted(() => {
  onDialogClosed()
})
</script>

<template>
  <Transition name="v-dialog-animation">
    <div
      v-if="model"
      ref="modal"
      class="bg-general-overlay-opacity-50 fixed top-0 left-0 z-50 flex h-screen w-full items-center overflow-y-auto"
      data-test="modal"
      tabindex="-1"
      @mousedown.self="onOverlayMouseDown"
      @mouseup.self="onOverlayMouseUp"
      @keyup.esc="close"
    >
      <Transition name="v-dialog-modal">
        <div
          v-if="model"
          class="v-dialog mx-auto inline-block max-h-screen min-h-3 w-auto"
          @mouseup="onDialogMousedown"
        >
          <div class="py-16">
            <div ref="dialog">
              <slot :close="close" />
            </div>
          </div>
        </div>
      </Transition>
    </div>
  </Transition>
</template>

<style>
@reference "../../style.css";

.v-dialog > * {
  @apply drop-shadow-xxl;
}

.v-dialog-animation-enter-active {
  transition: 150ms ease-out opacity;
}

.v-dialog-animation-enter-from {
  opacity: 0;
}

.v-dialog-animation-enter-to {
  opacity: 1;
}

.v-dialog-animation-enter-active .v-dialog {
  transition:
    150ms ease-in-out transform,
    150ms ease-in-out opacity;
}

.v-dialog-animation-enter-from .v-dialog {
  transform: translateY(30px);
  opacity: 0;
}

.v-dialog-animation-enter-to .v-dialog {
  transform: translateY(0);
  opacity: 1;
}

.v-dialog-animation-leave-active {
  transition: 133ms 66ms ease-out opacity;
}

.v-dialog-animation-leave-to {
  opacity: 0;
}

.v-dialog-animation-leave-from {
  opacity: 1;
}

.v-dialog-animation-leave-active .v-dialog {
  transition:
    150ms ease-in-out transform,
    150ms ease-in-out opacity;
}

.v-dialog-animation-leave-to .v-dialog {
  transform: translateY(-30px);
  opacity: 0;
}

.v-dialog-animation-leave-from .v-dialog {
  transform: translateY(0);
  opacity: 1;
}
</style>
